
import React, { useState, useEffect, useRef } from 'react';

interface Props {
  step: string;
  instruction: string;
  className?: string;
}

const Walkthrough: React.FC<Props> = ({ step, instruction, className = "" }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState<'left' | 'right'>('right');
  const [isInitial, setIsInitial] = useState(true);
  const [isWiggling, setIsWiggling] = useState(false);

  // DRAG STATE
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // Initial entrance animation
    const timer = setTimeout(() => setIsInitial(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setIsWiggling(true);
    const timer = setTimeout(() => setIsWiggling(false), 600);
    return () => clearTimeout(timer);
  }, [step]);

  const togglePosition = () => {
    setPosition(prev => prev === 'left' ? 'right' : 'left');
    setDragOffset({ x: 0, y: 0 }); // Reset drag on side toggle
  };

  // GLOBAL POINTER LISTENERS FOR ROBUST DRAGGING
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: PointerEvent) => {
      setDragOffset({
        x: e.clientX - startPos.current.x,
        y: e.clientY - startPos.current.y
      });
    };

    const handleUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [isDragging]);

  const handlePointerDown = (e: React.PointerEvent) => {
    // Ignore if clicking a button inside the widget
    if ((e.target as HTMLElement).closest('button')) return;

    // Only drag with primary touch/mouse
    if (!e.isPrimary) return;

    setIsDragging(true);
    startPos.current = {
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    };
  };

  return (
    <div
      style={{
        transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        touchAction: 'none',
        willChange: 'transform'
      }}
      className={`fixed z-[300] pointer-events-none ${isDragging ? '' : 'transition-opacity duration-500'} px-2 md:px-4 flex flex-col items-${position === 'left' ? 'start' : 'end'} 
        ${isInitial
          ? 'widget-initial'
          : `bottom-12 md:bottom-8 ${position === 'left' ? 'left-4' : 'right-4'} animate-in slide-in-from-bottom-4`
        } ${className}`}
    >
      {isMinimized ? (
        <button
          onPointerDown={handlePointerDown}
          onClick={() => { setIsMinimized(false); setDragOffset({ x: 0, y: 0 }); }}
          className={`pointer-events-auto bg-black text-white w-12 h-12 md:w-14 md:h-14 rounded-full shadow-2xl flex items-center justify-center border border-white/10 hover:scale-105 transition-transform ${isWiggling ? 'animate-wiggle' : ''}`}
        >
          <div className="relative">
            <span className="text-[9px] md:text-[10px] font-bold">Step {step}</span>
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
          </div>
        </button>
      ) : (
        <div
          onPointerDown={handlePointerDown}
          className={`pointer-events-auto bg-black text-white p-3 md:p-6 rounded-[1.5rem] md:rounded-[2rem] shadow-2xl border border-white/10 backdrop-blur-md w-full max-w-[220px] md:max-w-[320px] transition-transform ${isWiggling ? 'animate-wiggle' : ''}`}
        >
          <div className="flex items-center justify-between mb-2 md:mb-4">
            <div className="flex items-center space-x-2 text-indigo-400">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
              <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-widest opacity-60">Step {step}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => { e.stopPropagation(); togglePosition(); }}
                title="Move to other side"
                className="w-5 h-5 md:w-6 md:h-6 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <i className={`fas fa-exchange-alt text-[8px] md:text-[10px] opacity-40`}></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
                className="w-5 h-5 md:w-6 md:h-6 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <i className="fas fa-minus text-[8px] md:text-[10px] opacity-40"></i>
              </button>
            </div>
          </div>
          <p className="text-[12px] md:text-sm font-medium leading-relaxed mb-3 md:mb-4">
            {instruction}
          </p>
          <div className="flex items-center justify-between text-[7px] md:text-[9px] font-bold uppercase tracking-widest">
            <div className="flex items-center space-x-2 text-indigo-400">
              <i className="fas fa-info-circle"></i>
              <span>Demo</span>
            </div>
            <span className="text-white/10 hidden md:inline">Simulation</span>
          </div>
          <div className="mt-2 block md:hidden text-[6px] text-center opacity-20 font-bold tracking-[0.2em] uppercase">Drag to move</div>
        </div>
      )}
    </div>
  );
};

export default Walkthrough;
